import React from 'react';
import Box from '@nubank/nuds-web/components/Box/Box';
import styled from 'styled-components';
import Image from '@nubank/nuds-web/components/Image/Image';
import Typography from '@nubank/nuds-web/components/Typography/Typography';
import { useParallax } from 'react-scroll-parallax';
import Grid from '@nubank/nuds-web/components/Grid/Grid';

const StyledContainerGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr 1fr;
  /* stylelint-disable declaration-block-no-redundant-longhand-properties */
  grid-template-areas: 'image' 'title' 'detailRows';
  gap: 0;

  @media (width >= 1150px) {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 332px auto;
    grid-template-areas: 
    'image title'
    'detailRows detailRows';
    column-gap: 25px;
    row-gap: 120px;
  }
`;

const ImageContainer = styled.div`
  grid-area: image;

  @media (width >= 1150px) {
    img {
      border-radius: 12px;
      height: 120%;
    }
  }
`;

const TitleContainer = styled.div`
  grid-area: title;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding-left: 32px;
  padding-right: 32px;
  margin-top: -100px;

  @media (width >= 768px) {
    margin-top: -40%;
  }

  @media (width >= 1150px) {
    padding-left: 0;
    padding-right: 0;
    margin-top: 0;
    justify-content: space-between;
    height: 120%;

    div {
      height: 200px;
    }

    h1 {
      font-size: 2.6rem;
    }
  }
`;

const DetailRowsContainer = styled.div`
  grid-area: detailRows;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-left: 32px;
  margin-right: 32px;
  margin-top: 20px;

  @media (width >= 1150px) {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    width: 100%;
    margin-left: 0;
    margin-right: 0;
    margin-top: 0;
    gap: 32px;
  }
`;

const StyledIconImage = styled(Image)`
  width: 49px;
  height: 49px;
  min-width: 49px;

  @media (width >= 1151px) {
    width: 42px;
    height: 42px;
    min-width: 42px;
    margin-top: 4px;
  }
`;

const StyledTypography = styled(Typography)`
  @media (width >= 1151px) {
    font-size: 15px;
  }

  @media (width >= 1366px) {
    font-size: 16px;
  }
`;

const StyledSubtitleTypography = styled(Typography)`
  @media (width >= 1151px) {
    font-size: 1.3rem;
  }
`;

const StyledBoxTypography = styled(Typography)`
  @media (width >= 1151px) {
    font-size: 38px;
  }
`;

function Requirements() {
  const { ref } = useParallax({
    easing: [0, 0.9, 1, 1.4],
  });

  return (
    <Box
      tag="section"
      height="100%"
      width="100%"
      marginBottom={{ xs: '16x', lg: '100px' }}
      marginTop={{ xs: '0', lg: '48px' }}
      paddingHorizontal={{ xs: '0', lg: '56px' }}
    >
      <Grid
        paddingLeft={{ xs: '0', lg: '0' }}
        paddingRight={{ xs: '0', lg: '0' }}
      >

        <Grid.Child
          gridColumn={{ xs: '1 / span 4', md: '2 / span 4', lg: '2 / span 10' }}
          gridRow="1"
        >
          {/* BACK IMAGE */}

          <StyledContainerGrid>
            <ImageContainer>
              <Image
                alt="Dos personas viendo un telefono"
                srcSet={{
                  xs: 'credit-card/requirements/requirements-xs.jpg',
                  lg: 'credit-card/requirements/requirements-lg.jpg',
                }}
                height={{ xs: '85%', md: 'auto', lg: '100%' }}
                objectFit="cover"
                loading="lazy"
                webp
              />
            </ImageContainer>

            {/* TITLES */}

            <TitleContainer>
              <Box
                background="#F87"
                paddingVertical={{ xs: '32px', lg: '40px' }}
                paddingHorizontal={{ xs: '32px', lg: '50px' }}
                borderRadius="12px"
                ref={ref}
                display="flex"
                flexDirection="column"
                justifyContent={{ xs: 'auto', lg: 'center' }}
                alignItems={{ xs: 'auto', lg: 'center' }}
              >
                <StyledBoxTypography
                  color="white"
                  variant="heading1"
                  textAlign={{ xs: 'left', lg: 'center' }}
                  maxWidth={{ xs: 'auto', lg: '500px' }}
                >
                  No hay filas, solo unos cuantos clics
                </StyledBoxTypography>
              </Box>

              <Box
                background="#820AD1"
                paddingVertical={{ xs: '32px', lg: '40px' }}
                paddingHorizontal={{ xs: '32px', lg: '60px' }}
                borderRadius="12px"
                display="flex"
                flexDirection="column"
                justifyContent={{ xs: 'auto', lg: 'center' }}
                alignItems={{ xs: 'auto', lg: 'center' }}
              >
                <StyledBoxTypography
                  color="white"
                  variant="heading1"
                  whiteSpace="pre-line"
                  textAlign={{ xs: 'left', lg: 'center' }}
                >
                  {' ¿Todo listo para \n aplicar?'}
                </StyledBoxTypography>
              </Box>
            </TitleContainer>

            {/* ROW BOXES */}

            <DetailRowsContainer>
              <Box
                background="#F8F8FA"
                display="flex"
                borderRadius="12px"
                paddingHorizontal={{
                  xs: '20px',
                  lg: '24px',
                  xl: '40px',
                }}
                paddingVertical={{
                  xs: '30px',
                  lg: '24px',
                  xl: '40px',
                }}
              >
                <StyledIconImage
                  webP={false}
                  src="credit-card/requirements/paper-icon.svg"
                  alt="Prepara tus documentos"
                  loading="lazy"
                />
                <Box
                  display="flex"
                  flexDirection="column"
                  marginLeft="20px"
                >
                  <StyledSubtitleTypography
                    strong
                    marginBottom="8px"
                    whiteSpace={{ xs: 'normal', lg: 'pre-line' }}
                  >
                    {'Prepara tus \n documentos'}
                  </StyledSubtitleTypography>
                  <StyledTypography>
                    Necesitas tu identificación oficial
                    (INE, pasaporte o tarjeta de residencia), y tomarte una selfie.
                  </StyledTypography>
                </Box>
              </Box>

              <Box
                background="#F8F8FA"
                display="flex"
                borderRadius="12px"
                paddingHorizontal={{
                  xs: '20px',
                  lg: '24px',
                  xl: '40px',
                }}
                paddingVertical={{
                  xs: '30px',
                  lg: '24px',
                  xl: '40px',
                }}
              >
                <StyledIconImage
                  webP={false}
                  src="credit-card/requirements/phone-icon.svg"
                  alt="Aplica desde la pagina"
                  loading="lazy"
                />
                <Box
                  display="flex"
                  flexDirection="column"
                  marginLeft="20px"
                >
                  <StyledSubtitleTypography strong marginBottom="8px" whiteSpace={{ xs: 'normal', lg: 'pre-line' }}>
                    {'Aplica desde la \n pagina o la app'}
                  </StyledSubtitleTypography>
                  <StyledTypography>
                    Solicita la tarjeta de crédito Nu en línea en esta página o descargando la app.
                  </StyledTypography>
                </Box>
              </Box>

              <Box
                background="#F8F8FA"
                display="flex"
                borderRadius="12px"
                paddingHorizontal={{
                  xs: '20px',
                  lg: '24px',
                  xl: '40px',
                }}
                paddingVertical={{
                  xs: '30px',
                  lg: '24px',
                  xl: '40px',
                }}
              >
                <StyledIconImage
                  webP={false}
                  src="credit-card/requirements/mail-icon.svg"
                  alt="Aumenta tus posibilidades"
                  loading="lazy"
                />
                <Box
                  display="flex"
                  flexDirection="column"
                  marginLeft="20px"
                >
                  <StyledSubtitleTypography strong marginBottom="8px" whiteSpace={{ xs: 'normal', lg: 'pre-line' }}>
                    {'Aumenta tus \n posibilidades'}
                  </StyledSubtitleTypography>
                  <StyledTypography>
                    Aplica a la tarjeta con el enlace invitación de alguien con Nu,
                    así será más probable obtenerla.
                  </StyledTypography>
                </Box>
              </Box>
            </DetailRowsContainer>

          </StyledContainerGrid>
        </Grid.Child>
      </Grid>
    </Box>

  );
}

export default Requirements;
